import React, { useState, useEffect, useRef } from 'react';
import {
    Card, CardBody, Container, Form, Row, Col,
    Label, Input, FormFeedback, Spinner
} from 'reactstrap';
import { Btn } from '../../AbstractElements';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { apiPost, uploadFile } from '../../Utils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../Context/AuthContext';
import { useConfig } from '../../Context/ConfigContext';
import { useNavigate } from 'react-router-dom';
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Logo must be exactly this size
const LOGO_WIDTH = 300;
const LOGO_HEIGHT = 98;

interface SettingForm {
    company_name: string;
    address: string;
    contact_number: string;
    email: string;
    currency: string;
    user_term_condition: string;
    user_privacy_policy: string;
    bystander_term_condition: string;
    bystander_privacy_policy: string;
    base_hourly_rate: string;
}

interface Errors {
    company_name?: string;
    address?: string;
    contact_number?: string;
    email?: string;
    currency?: string;
    logo?: string;
    user_term_condition?: string;
    user_privacy_policy?: string;
    bystander_term_condition?: string;
    bystander_privacy_policy?: string;
    base_hourly_rate?: string;
}

const AddGeneralSettings = () => {
    const { hasPermission } = useAuth();
    const { refreshConfig } = useConfig();
    const canAdd = hasPermission('general-settings.add');
    const navigate = useNavigate();

    const [form, setForm] = useState<SettingForm>({
        company_name: '',
        address: '',
        contact_number: '',
        email: '',
        currency: '',
        user_term_condition: '',
        user_privacy_policy: '',
        bystander_term_condition: '',
        bystander_privacy_policy: '',
        base_hourly_rate: '',
    });

    const customEditorConfig = {
        toolbar: [
            "heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "|", "insertTable", "undo", "redo"
        ]
    };
    const [errors, setErrors] = useState<Errors>({});
    const [saving, setSaving] = useState(false);

    // Logo state
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const handlePhoneChange = (value: string) => {
        setForm(prev => ({ ...prev, contact_number: value }));
        setErrors(prev => ({ ...prev, contact_number: undefined }));
    };

    const validatePhoneNumber = (value: string) => {
        if (!value) {
            return "Contact number is required.";
        }
        const sanitizedValue = value.replace(/\D/g, "");
        const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);
        if (!phoneNumber || !phoneNumber.isValid()) {
            return "Invalid contact number.";
        }
        const allowedCountries: CountryCode[] = ["IN"];
        if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
            return "Only Indian numbers are allowed.";
        }
        return true;
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
            setErrors(prev => ({ ...prev, logo: 'Only image files are allowed.' }));
            setLogoFile(null);
            setLogoPreview(null);
            return;
        }

        // Validate dimensions
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            if (img.width !== LOGO_WIDTH || img.height !== LOGO_HEIGHT) {
                URL.revokeObjectURL(objectUrl);
                setErrors(prev => ({
                    ...prev,
                    logo: `Logo must be exactly ${LOGO_WIDTH}×${LOGO_HEIGHT} px. Your image is ${img.width}×${img.height} px.`
                }));
                setLogoFile(null);
                setLogoPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            } else {
                setErrors(prev => ({ ...prev, logo: undefined }));
                setLogoFile(file);
                setLogoPreview(objectUrl);
            }
        };
        img.src = objectUrl;
    };

    const validate = (): boolean => {
        const newErrors: Errors = {};
        if (!form.company_name.trim()) newErrors.company_name = 'Company name is required.';
        if (!form.address.trim()) newErrors.address = 'Address is required.';

        const phoneValidationResult = validatePhoneNumber(form.contact_number);
        if (phoneValidationResult !== true) {
            newErrors.contact_number = phoneValidationResult as string;
        }

        if (!form.user_term_condition.trim()) newErrors.user_term_condition = 'User Terms & Conditions is required.';
        if (!form.user_privacy_policy.trim()) newErrors.user_privacy_policy = 'User Privacy Policy is required.';
        if (!form.bystander_term_condition.trim()) newErrors.bystander_term_condition = 'Bystander Terms & Conditions is required.';
        if (!form.bystander_privacy_policy.trim()) newErrors.bystander_privacy_policy = 'Bystander Privacy Policy is required.';
        if (!form.base_hourly_rate.trim()) newErrors.base_hourly_rate = 'Base hourly rate is required.';
        else if (isNaN(Number(form.base_hourly_rate)) || Number(form.base_hourly_rate) < 0) newErrors.base_hourly_rate = 'Enter a valid positive number.';

        if (!form.email.trim()) newErrors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email address.';
        if (!form.currency) newErrors.currency = 'Please select a currency.';
        if (!logoFile && !errors.logo) newErrors.logo = 'Dashboard Logo is required.';
        if (errors.logo) newErrors.logo = errors.logo; // carry over dimension error
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            let logoFilename = '';
            if (logoFile) {
                logoFilename = await uploadFile(logoFile);
            }

            const payload = {
                ...form,
                logo: logoFilename
            };

            await apiPost('settings/store', payload);

            await refreshConfig();
            toast.success('Settings saved successfully!', { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/general_settings`);
            }, 1000);
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add General Settings" parent="Master Settings" title="Add General Settings" />
            <Container fluid>
                <Card className="shadow-sm">
                    <CardBody>
                        {!canAdd ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa fa-lock fa-2x mb-2 d-block"></i>
                                You do not have permission to add General Settings.
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit} noValidate>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Label className="fw-semibold">Company Name <span className="text-danger">*</span></Label>
                                        <Input name="company_name" value={form.company_name} onChange={handleChange} placeholder="Enter company name" invalid={!!errors.company_name} autoComplete="off" />
                                        <FormFeedback>{errors.company_name}</FormFeedback>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="fw-semibold">Email <span className="text-danger">*</span></Label>
                                        <Input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Enter email address" invalid={!!errors.email} autoComplete="off" />
                                        <FormFeedback>{errors.email}</FormFeedback>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="fw-semibold">Contact Number <span className="text-danger">*</span></Label>
                                        <PhoneInput disableDropdown={true} country={"in"} onlyCountries={["in"]} value={form.contact_number} onChange={handlePhoneChange} inputProps={{ name: "contact_number", placeholder: "Enter Contact Number", autoComplete: "none" }} inputStyle={{ width: '100%', borderColor: errors.contact_number ? '#dc3545' : '#ced4da' }} />
                                        {errors.contact_number && <FormFeedback style={{ display: 'block' }}>{errors.contact_number}</FormFeedback>}
                                    </Col>
                                    <Col md={6}>
                                        <Label className="fw-semibold">Currency <span className="text-danger">*</span></Label>
                                        <Input name="currency" value={form.currency} onChange={handleChange} placeholder="Enter currency" invalid={!!errors.currency} autoComplete="off" />
                                        <FormFeedback>{errors.currency}</FormFeedback>
                                    </Col>
                                    <Col md={6}>
                                        <Label className="fw-semibold">Base Hourly Rate <span className="text-danger">*</span></Label>
                                        <Input type="number" name="base_hourly_rate" value={form.base_hourly_rate} onChange={handleChange} placeholder="Enter base hourly rate" invalid={!!errors.base_hourly_rate} autoComplete="off" />
                                        <FormFeedback>{errors.base_hourly_rate}</FormFeedback>
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">Address <span className="text-danger">*</span></Label>
                                        <Input type="textarea" name="address" value={form.address} onChange={handleChange} placeholder="Enter full address" rows={3} invalid={!!errors.address} autoComplete="off" />
                                        <FormFeedback>{errors.address}</FormFeedback>
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">Dashboard Logo Upload <span className="text-danger">*</span> <span className="text-muted fw-normal" style={{ fontSize: '12px' }}>(Exact size: {LOGO_WIDTH}×{LOGO_HEIGHT} px)</span></Label>
                                        <div className="rounded d-flex align-items-center justify-content-center" style={{ height: '90px', cursor: 'pointer', borderWidth: '1px', borderStyle: errors.logo ? 'solid' : 'dashed', borderColor: errors.logo ? '#dc3545' : '#ced4da', backgroundColor: '#f8f9fa', transition: 'border-color 0.2s' }} onClick={() => fileInputRef.current?.click()}>
                                            {logoPreview ? <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '70px' }} /> : <div className="text-center text-muted"><i className="fa fa-upload fa-lg mb-1 d-block"></i><small>Click to upload logo</small></div>}
                                        </div>
                                        <input type="file" ref={fileInputRef} accept="image/*,image/svg+xml" style={{ display: 'none' }} onChange={handleLogoChange} />
                                        {errors.logo && <div className="text-danger mt-1" style={{ fontSize: '13px' }}>{errors.logo}</div>}
                                        {!errors.logo && logoFile && <div className="text-success mt-1" style={{ fontSize: '12px' }}>✓ Logo is valid ({LOGO_WIDTH}×{LOGO_HEIGHT} px)</div>}
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">User Terms & Conditions <span className="text-danger">*</span></Label>
                                        <div style={{ border: errors.user_term_condition ? "1px solid #dc3545" : "1px solid #ced4da", borderRadius: "4px" }}>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={customEditorConfig}
                                                data={form.user_term_condition}
                                                onChange={(event: any, editor: any) => {
                                                    const data = editor.getData();
                                                    setForm(prev => ({ ...prev, user_term_condition: data }));
                                                    setErrors(prev => ({ ...prev, user_term_condition: undefined }));
                                                }}
                                            />
                                        </div>
                                        {errors.user_term_condition && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.user_term_condition}</div>}
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">User Privacy Policy <span className="text-danger">*</span></Label>
                                        <div style={{ border: errors.user_privacy_policy ? "1px solid #dc3545" : "1px solid #ced4da", borderRadius: "4px" }}>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={customEditorConfig}
                                                data={form.user_privacy_policy}
                                                onChange={(event: any, editor: any) => {
                                                    const data = editor.getData();
                                                    setForm(prev => ({ ...prev, user_privacy_policy: data }));
                                                    setErrors(prev => ({ ...prev, user_privacy_policy: undefined }));
                                                }}
                                            />
                                        </div>
                                        {errors.user_privacy_policy && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.user_privacy_policy}</div>}
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">Bystander Terms & Conditions <span className="text-danger">*</span></Label>
                                        <div style={{ border: errors.bystander_term_condition ? "1px solid #dc3545" : "1px solid #ced4da", borderRadius: "4px" }}>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={customEditorConfig}
                                                data={form.bystander_term_condition}
                                                onChange={(event: any, editor: any) => {
                                                    const data = editor.getData();
                                                    setForm(prev => ({ ...prev, bystander_term_condition: data }));
                                                    setErrors(prev => ({ ...prev, bystander_term_condition: undefined }));
                                                }}
                                            />
                                        </div>
                                        {errors.bystander_term_condition && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.bystander_term_condition}</div>}
                                    </Col>
                                    <Col md={12}>
                                        <Label className="fw-semibold">Bystander Privacy Policy <span className="text-danger">*</span></Label>
                                        <div style={{ border: errors.bystander_privacy_policy ? "1px solid #dc3545" : "1px solid #ced4da", borderRadius: "4px" }}>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                config={customEditorConfig}
                                                data={form.bystander_privacy_policy}
                                                onChange={(event: any, editor: any) => {
                                                    const data = editor.getData();
                                                    setForm(prev => ({ ...prev, bystander_privacy_policy: data }));
                                                    setErrors(prev => ({ ...prev, bystander_privacy_policy: undefined }));
                                                }}
                                            />
                                        </div>
                                        {errors.bystander_privacy_policy && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.bystander_privacy_policy}</div>}
                                    </Col>
                                </Row>
                                <div className="mt-4 d-flex justify-content-start">
                                    <Btn color="primary" type="submit" disabled={saving}>{saving ? <> Submiting <Spinner size="sm" /></> : 'Submit'}</Btn>
                                </div>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
};

export default AddGeneralSettings;
